'use client';
import { useRef } from 'react';
import { requestAnimationFrame } from '@s7/platform';

/**
 * Предназначен для привязки элемента DOM к ref и выполнения
 * заданного действия при инициализации ref.
 *
 * Полезно, когда доступ к узлу необходимо получить сразу при монтировании,
 * не используя эффект для отслеживания инициализации узла.
 *
 * @typeParam TNode - Тип узла, к которому будет привязан ref.
 *
 * @param initialize - Условие для выполнения инициализации.
 * @param onNodeAttached - Выполняется при инициализации узла.
 *
 * @returns Кортеж, содержащий функцию-инициализатор и ref.
 *
 * @primaryExample
 * const [initializer, ref] = useAttachedRef(true, (node) => {
 *     // Действие при инициализации узла
 *     node.style.color = 'red';
 * });
 *
 * // <div ref={initializer}>Пример текста</div>
 *
 * @example
 *
 * ```ts
 * const [isInitialized, setIsInitialized] = useState(false);
 * const [initializer, ref] = useAttachedRef(isInitialized, (node) => {
 *     node.classList.add('initialized');
 * });
 *
 * useEffect(() => {
 *     setTimeout(() => setIsInitialized(true), 5000);
 * }, []);
 * ```
 *
 * ```jsx
 * <div ref={initializer}>Другой пример текста</div>
 * ```
 *
 * @note
 * `onNodeAttached` будет вызван только один раз при первом присоединении узла,
 * если параметр `initialize` установлен в `true`.
 */ function useAttachedRef(initialize, onNodeAttached) {
    const ref = useRef(null);
    const shouldInitializeRef = useRef(true);
    const initializer = (node)=>{
        if (node) {
            ref.current = node;
            if (shouldInitializeRef.current && initialize) {
                /**
                 * Важно: `onNodeAttached` является эффектом, который напрямую не управляется React
                 * - этот эффект не встраивается в жизненный цикл корректно сам по себе.
                 *
                 * `useAttachedRef` добивается того, чтобы `onNodeAttached` выполнялся после монтирования, но до первого обновления.
                 *
                 * Нам необходимо откладывать выполнение этого эффекта, чтобы стили узла гарантированно были вычислены,
                 * в противном случае мы можем столкнуться с проблемами, в частности при работе с анимациями и жестами.
                 *
                 * Также один из возможных вариантов решения:
                 * ```
                 * window.getComputedStyle(node).padding // любое свойство вместо padding.
                 * // Стили вычисляются лениво, свойства являются геттерами.
                 * // Вычисление произвольного свойства приводит к вычислению стилей узла.
                 * ```
                 * */ requestAnimationFrame(()=>onNodeAttached(node));
                shouldInitializeRef.current = false;
            }
        }
    };
    return [
        initializer,
        ref
    ];
}

export { useAttachedRef };
//# sourceMappingURL=useAttachedRef.js.map
